import { useEffect, useState } from "react";
import Link from "next/link";
import Button from "../../Button";
import CandidateCarousel from "./CandidateCarousel";
import CandidateInfoSnippet from "./CandidateInfoSnippet";
import styles from "../../../styles/candidate/parts/CandidateInfoListItem.module.scss";

const CandidateInfoListItem = ({
  index,
  data,
  innerRef,
  type,
  compareButtonLabel,
  contestName,
  candidacyId
}) => {
  const [isActive, setIsActive] = useState(true);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [previewFeed, setPreviewFeed] = useState([]);

  const fetcher = (url) => fetch(url).then((res) => res.json());

  useEffect(() => {
    const generatePreviews = async () => {
      if (data.urls) {
        setIsFeedLoading(true);
        const urlMeta = await Promise.all(data.urls.map(async url => {
          const urlPreview = `/api/url/preview?url=${url}`;

          try {
            const urlData = await fetcher(urlPreview);
            return {
              link: url,
              thumbnail: urlData['og:image'],
              title: urlData.title,
              dateTime: urlData['article:modified_time'] || urlData['article:published_time']
            };
          } catch (err) {
            return null;
          }
        }));

        setPreviewFeed(urlMeta.filter(m => m != null || m != undefined));
        setIsFeedLoading(false);
      }
    }

    generatePreviews();
  }, [data.urls]);

  return (
    <div id={innerRef} className={styles.candidateInfoListItem}>
      {
        type === "accordionItem" &&
        <div className={styles.candidateInfoListItemAccordion}>
          <div className={styles.candidateInfoItemHeader}>
            <p>{data.title}</p>
          </div>
          <div className={styles.candidateInfoItemBody}>
            <p>{data.body}</p>
            {data.source && (
              <p className={styles.source}>
                Source (<a href={data.source.url}>{data.source.name}</a>)
              </p>
            )}
            {(data.feed || previewFeed) && <CandidateCarousel items={data.feed || previewFeed} isFeedLoading={isFeedLoading || false} />}
          </div>
        </div>
      }
      {type === "profileScoop" && (
        <div className={styles.candidateInfoListItemScoop}>
          <Link href={`https://www.rappler.com/people/${data.wp_slug}`}>
            <a target="_blank" className={styles.textLink}>
              Want the full scoop? Check out the profile of{" "}
              {`${data.alternate_name || data.first_name} ${data.last_name}`}
            </a>
          </Link>
          <Button
            url={`https://www.rappler.com/people/${data.wp_slug}`}
            label={"Go to Profile"}
          />
        </div>
      )}
      {type === "specialListItem" && (
        <div className={styles.candidateInfoListItemSpecial}>
          <p className={styles.textLink}>{data.content}</p>
          <Button
            url={`https://www.rappler.com/people/${data.wp_slug}`}
            label={`Read ${data.alternate_name || data.first_name}${
              (data.alternate_name || data.first_name)
                .split("")
                .pop()
                .toLowerCase() === "s"
                ? "'"
                : "'s"
            } profile`}
          />
        </div>
      )}
      {type === "article" && (
        <div className={styles.candidateInfoListItemArticle}>
            <div className={styles.section}>
              { data.category &&
                <div className={styles.header}>
                  <h4 className={styles.title}>{data.category}</h4>
                </div>
              }
              <div key={`subsection-${index}`} className={styles.subsection}>
                <h5 className={styles.title}>{data.title}</h5>
                {data.body && <p className={styles.description}>{data.body}</p>}
                {data.table &&
                  <table className={data.table.type === "comic" ? styles.table_comic : styles.table_classic}>
                    <thead>
                      <tr>
                        {
                            data.table.columns.map((column, index) => {
                            return <th key={`column-${index}`}>{column}</th>
                          })
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {data.table.data.map((tableContentRow, index) => {
                        return (
                          <tr key={`row-${index}`}>
                            {
                              tableContentRow.map((tableContentData, index) => {
                                return (
                                  <td key={`data-${index}`}>{tableContentData}</td>
                                )
                              })
                            }
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                }
                {data.feed && <CandidateCarousel items={data.feed} />}
              </div>
          </div>
        </div>
      )}
      {type === "snippet" && (
        <div>
        { data.header && data.header.title &&
          <div className={styles.itemHeader}>
            <h5 className={styles.itemTitle}>{ data.header.title.toUpperCase() }</h5>
            { compareButtonLabel &&
              <Link
                href={{
                  pathname: "/elections/2022/races/president-vice-president/tale-of-the-tape",
                  query: {
                    selectedCandidates: `${candidacyId}`,
                    race: contestName === "PRESIDENT PHILIPPINES" ? "presidential" : "vice-presidential"
                  }
                }}
              >
                <a className={styles.compareButton}>
                  { compareButtonLabel }
                </a>
              </Link>
            }
          </div>
        }
        <CandidateInfoSnippet
            header={data.header}
            details={data.details}
            context={data.context}
            source={data.source}
            showSocials={false}
        />
        { data.source && data.source.url &&
          <div className={styles.footNote}>
            <p>
              Source&nbsp;
              <Link href={data.source.url} passHref>
                {data.source.text}
              </Link>
            </p>
          </div>
        }
        {data.feed && <CandidateCarousel items={data.feed} />}
        {!data.feed && <div className={styles.noFeedPadding}></div>}
        </div>
      )}
      {type === "position" && (
        <div>
          <CandidateInfoSnippet
            header={data.header}
            details={data.details}
            context={data.context}
            isPosition
            showSocials={false}
            showTitleInside
            isSmall={false}
            positionStrength={data.positionStrength}
            contestName={contestName}
            candidacyId={candidacyId}
          />
          {data.feed && <CandidateCarousel items={data.feed} />}
          {!data.feed && <div className={styles.noFeedPadding}></div>}
        </div>
      )}
    </div>
  );
};

export default CandidateInfoListItem;
